// @ts-check
// "Conectar com a Meta" na janela da escola (contrato 08 de leads e WhatsApp, seção 8.1). O popup abre no clique (para
// os bloqueadores e o iOS) em branco, recebe o endereço do portal com o passe e conversa com esta janela só por
// postMessage: o resultado vale quando vem da origem do portal, do popup aberto aqui e da tentativa atual. Ao READY do
// popup, esta janela responde com a prova do passe (aperto de mão), e só então o portal libera o login da Meta. A SPA
// da escola não carrega o SDK da Meta. Uma tentativa por página: enquanto um botão espera a Meta, os outros ficam
// desabilitados, e quem espera pode trazer a janela da Meta de volta ou cancelar.
import { html } from 'htm/preact';
import { signal } from '@preact/signals';
import { useEffect, useRef, useState } from 'preact/hooks';
import { requestMetaSignupPass } from '../../../data/integrations.js';
import { Button } from '../../../ui/button.js';
import { Icon } from '../../../ui/icon.js';
import { showSuccessSnackbar } from '../../../ui/snackbar.js';
import { describeError } from '../integration-ui.js';
import { META_SIGNUP_ACK_TYPE, isMetaSignupResultMessage } from '../../../../../shared/meta-signup/embedded-signup-events.js';
import { isActiveMetaSignupStatus, isMetaSignupLockedByOther, metaSignupAttemptActions } from '../../../lib/meta-signup-attempt.js';
import { hasTranslation, t } from '../../../i18n/index.js';

const POPUP_NAME = 'appcrm-meta-signup';
const POPUP_FEATURES = 'popup,width=600,height=760';
const POPUP_WATCH_MILLISECONDS = 500;
// dono da tentativa viva na página (id da instância): os botões dividem o mesmo popup e o passe novo encerra o anterior
const activeAttemptOwner = signal(/** @type {string | null} */ (null));

/**
 * @typedef {'IDLE' | 'OPENING' | 'WAITING' | 'BLOCKED' | 'CLOSED' | 'CANCELLED' | 'FAILED' | 'EXPIRED'} ConnectStatus
 * @typedef {{ channelId: string | null, displayPhoneNumber: string, isCoexistence: boolean, warnings: string[] }} MetaSignupResult
 */

/** 16 bytes aleatórios em base64url: identifica a tentativa na mensagem do portal. */
export function generateAttemptId() {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
}

/**
 * Texto de um cancelamento ou de uma falha devolvidos pelo portal.
 * @param {{ status: string, cancelledStep?: string | null, errorCode?: string | null }} data
 */
export function describeMetaSignupOutcome(data) {
  if (data.status === 'CANCELLED') {
    const stepKey = `metaSignup.steps.${data.cancelledStep}`;
    const step = data.cancelledStep ? (hasTranslation(stepKey) ? t(stepKey) : data.cancelledStep) : null;
    return step ? t('metaSignup.cancelledAtStep', { step }) : t('metaSignup.cancelled');
  }
  if (data.errorCode === 'META_ERROR') return t('metaSignup.metaError');
  if (data.errorCode === 'SDK_FAILED') return t('metaSignup.sdkFailed');
  const errorKey = `errors.${data.errorCode ?? 'UNKNOWN'}`;
  return hasTranslation(errorKey) ? t(errorKey) : t('errors.UNKNOWN');
}

/**
 * @param {{ metaSignup: { portalOrigin: string }, title?: string, variant?: 'primary' | 'secondary', icon?: string,
 *   onConnected: (result: MetaSignupResult) => void }} props
 */
export function MetaSignupConnect({ metaSignup, title, variant = 'primary', icon = 'IconBrandWhatsapp', onConnected }) {
  const [status, setStatus] = useState(/** @type {ConnectStatus} */ ('IDLE'));
  const [message, setMessage] = useState(/** @type {string | null} */ (null));
  const popupRef = useRef(/** @type {Window | null} */ (null));
  const attemptRef = useRef('');
  const connectUrlRef = useRef('');
  // prova do passe: só em memória, entregue ao popup do portal na resposta ao READY
  const proofRef = useRef('');
  const expiresAtRef = useRef(0);
  const intervalRef = useRef(/** @type {ReturnType<typeof setInterval> | null} */ (null));
  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;
  const statusRef = useRef(status);
  statusRef.current = status;
  const ownerIdRef = useRef('');
  if (!ownerIdRef.current) ownerIdRef.current = generateAttemptId();

  const stopWatching = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
  };

  const claimAttempt = () => {
    activeAttemptOwner.value = ownerIdRef.current;
  };
  const releaseAttempt = () => {
    if (activeAttemptOwner.value === ownerIdRef.current) activeAttemptOwner.value = null;
  };

  /** @param {ConnectStatus} next @param {string | null} [text] */
  const finish = (next, text = null) => {
    stopWatching();
    setStatus(next);
    setMessage(text);
    if (!isActiveMetaSignupStatus(next)) releaseAttempt();
  };

  const watchPopup = () => {
    stopWatching();
    intervalRef.current = setInterval(() => {
      if (Date.now() > expiresAtRef.current) {
        finish('EXPIRED', t('metaSignup.expired'));
        return;
      }
      const popup = popupRef.current;
      if (popup && popup.closed && (statusRef.current === 'OPENING' || statusRef.current === 'WAITING')) finish('CLOSED', t('metaSignup.popupClosed'));
    }, POPUP_WATCH_MILLISECONDS);
  };

  /**
   * Pede um passe para uma tentativa nova e leva o popup (já aberto) ao portal.
   * @param {Window | null} popup
   */
  const openAttempt = async (popup) => {
    const attemptId = generateAttemptId();
    attemptRef.current = attemptId;
    setStatus('OPENING');
    setMessage(null);
    let pass;
    try {
      pass = await requestMetaSignupPass(attemptId);
    } catch (error) {
      if (attemptRef.current !== attemptId) return;
      try {
        if (popup && !popup.closed && popup.location.href === 'about:blank') popup.close();
      } catch {
        // popup já no portal (nova tentativa): fica aberto com a mensagem dele
      }
      finish('FAILED', describeError(error));
      return;
    }
    // cancelada enquanto o passe era pedido: o popup já foi fechado
    if (attemptRef.current !== attemptId) return;
    connectUrlRef.current = pass.connectUrl;
    proofRef.current = pass.proof;
    expiresAtRef.current = Date.parse(pass.expiresAt) || Date.now() + 15 * 60 * 1000;
    if (!popup || popup.closed) {
      popupRef.current = null;
      finish('BLOCKED', t('metaSignup.popupBlocked'));
      return;
    }
    popupRef.current = popup;
    popup.location.replace(pass.connectUrl);
    setStatus('WAITING');
    watchPopup();
  };

  useEffect(() => {
    /** @param {MessageEvent} event */
    const handleMessage = (event) => {
      if (!isMetaSignupResultMessage(event, { portalOrigin: metaSignup.portalOrigin, popup: popupRef.current, attemptId: attemptRef.current })) return;
      const data = event.data;
      if (data.status === 'READY') {
        // aperto de mão: a resposta vai só para o popup desta tentativa, na origem exata do portal
        /** @type {Window} */ (event.source).postMessage({ type: META_SIGNUP_ACK_TYPE, attemptId: attemptRef.current, proof: proofRef.current }, metaSignup.portalOrigin);
        if (statusRef.current === 'OPENING') setStatus('WAITING');
      } else if (data.status === 'CONNECTED') {
        finish('IDLE');
        const number = String(data.displayPhoneNumber ?? '');
        showSuccessSnackbar(t('metaSignup.connectedSnackbar', { number }));
        onConnectedRef.current({
          channelId: data.channelId ?? null, displayPhoneNumber: number, isCoexistence: Boolean(data.isCoexistence),
          warnings: Array.isArray(data.warnings) ? data.warnings.map(String) : [],
        });
      } else if (data.status === 'CANCELLED' || data.status === 'FAILED') {
        finish(data.status, describeMetaSignupOutcome(data));
      } else if (data.status === 'RETRY_REQUESTED') {
        // o passe é de uso único: a escola pede outro e troca o endereço do mesmo popup
        if (isMetaSignupLockedByOther(activeAttemptOwner.value, ownerIdRef.current)) return;
        claimAttempt();
        openAttempt(popupRef.current);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      stopWatching();
      releaseAttempt();
    };
  }, [metaSignup.portalOrigin]);

  const start = () => {
    if (isMetaSignupLockedByOther(activeAttemptOwner.value, ownerIdRef.current)) return;
    claimAttempt();
    // o reaproveitamento evita uma segunda janela quando a pessoa tenta de novo com o popup ainda aberto
    const current = popupRef.current && !popupRef.current.closed ? popupRef.current : null;
    const popup = current ?? window.open('about:blank', POPUP_NAME, POPUP_FEATURES);
    popup?.focus();
    // já guardado: "Mostrar a janela da Meta" e "Cancelar" valem enquanto o passe é pedido
    popupRef.current = popup;
    openAttempt(popup);
  };

  const openBlocked = () => {
    const popup = window.open(connectUrlRef.current, POPUP_NAME, POPUP_FEATURES);
    if (!popup) return;
    popupRef.current = popup;
    setStatus('WAITING');
    setMessage(null);
    watchPopup();
  };

  // a janela da Meta ficou atrás da janela da escola
  const focusPopup = () => {
    const popup = popupRef.current;
    if (popup && !popup.closed) popup.focus();
    else if (statusRef.current === 'WAITING') finish('CLOSED', t('metaSignup.popupClosed'));
  };

  // desistir: fecha o popup e esquece a tentativa (mensagens atrasadas dela deixam de valer)
  const cancel = () => {
    try {
      if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
    } catch {
      // janela já fechada pelo navegador
    }
    popupRef.current = null;
    attemptRef.current = '';
    proofRef.current = '';
    finish('IDLE');
  };

  const isBusy = status === 'OPENING' || status === 'WAITING';
  const canRetry = status === 'CLOSED' || status === 'CANCELLED' || status === 'FAILED' || status === 'EXPIRED';
  const isLockedByOther = isMetaSignupLockedByOther(activeAttemptOwner.value, ownerIdRef.current);
  const actions = metaSignupAttemptActions(status);
  return html`<div class="meta-signup-connect" data-status=${status}>
    <div class="meta-signup-connect-row">
      ${status === 'BLOCKED'
        ? html`<${Button} title=${t('metaSignup.openPopup')} icon="IconExternalLink" accent="blue" size="small" onClick=${openBlocked} />`
        : html`<${Button} title=${canRetry ? t('metaSignup.retry') : title ?? t('metaSignup.connect')} icon=${canRetry ? 'IconRefresh' : icon}
            variant=${variant} accent=${variant === 'primary' ? 'blue' : 'default'} size="small" isLoading=${isBusy} disabled=${isLockedByOther} onClick=${start} />`}
    </div>
    ${actions.canCancel
      ? html`<div class="meta-signup-connect-row meta-signup-connect-attempt">
          ${isBusy
            ? html`<span class="meta-signup-connect-status" role="status">${status === 'OPENING' ? t('metaSignup.opening') : t('metaSignup.waiting')}</span>`
            : null}
          ${actions.canFocusPopup
            ? html`<${Button} title=${t('metaSignup.focusPopup')} icon="IconAppWindow" variant="tertiary" size="small" onClick=${focusPopup} />`
            : null}
          <${Button} title=${t('metaSignup.cancelAttempt')} icon="IconX" variant="tertiary" size="small" onClick=${cancel} />
        </div>`
      : null}
    ${message
      ? html`<p class="meta-signup-connect-message" role=${status === 'BLOCKED' ? 'status' : 'alert'} data-tone=${status === 'BLOCKED' || status === 'CLOSED' ? 'info' : 'warning'}>
          <${Icon} name=${status === 'BLOCKED' || status === 'CLOSED' ? 'IconInfoCircle' : 'IconAlertTriangle'} size=${14} /><span>${message}</span>
        </p>`
      : null}
  </div>`;
}
